import { Component, forwardRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { ApiError } from '../../../../core';
import { ConcorrenteApi } from '../../../environment/concorrentes/apis/concorrente.api';
import { EnumSituacaoConcorrente } from '../../../environment/concorrentes/enums/situacao-concorrente.enum';
import { IConcorrenteListItem } from '../../../environment/concorrentes/interfaces/concorrente-list.interface';
import { IConcorrenteRef } from '../../../environment/concorrentes/interfaces/concorrente-ref.interface';
import { resolverIdentidadeConcorrente } from '../../../environment/concorrentes/utils/concorrente-identidade.util';
import { CustomControlValueAccessor } from '../../utils/custom-control-value-accessor/custom-control-value-accessor';
import { ISelectOption } from '../select/models/select-option.interface';
import { ISelectSettings } from '../select/models/select-settings.interface';
import { ToastService } from '../toast/services/toast.service';

export interface IConcorrenteSelectOption extends ISelectOption {
  imagemUrl: string | null;
  icone: string | null;
  tipoLabel: string | null;
  inativo: boolean;
}

const mesmosIds = (anteriores: number[] | undefined, atuais: number[] | undefined): boolean =>
  (anteriores ?? []).length === (atuais ?? []).length && (anteriores ?? []).every((id, i) => id === (atuais ?? [])[i]);

@Component({
  selector: 'sgt-concorrente-select',
  templateUrl: './concorrente-select.component.html',
  styleUrls: ['./concorrente-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ConcorrenteSelectComponent),
      multi: true,
    },
  ],
})
export class ConcorrenteSelectComponent
  extends CustomControlValueAccessor<number>
  implements OnChanges, OnDestroy {
  @Input() concorrenteVinculado: IConcorrenteRef | null = null;
  @Input() idsJaSelecionados: number[] = [];

  settings: Partial<ISelectSettings>;
  options: IConcorrenteSelectOption[] = [];
  selectedId: number | null = null;

  private readonly defaultSettings: Partial<ISelectSettings> = {
    placeholder: 'Selecione um concorrente',
    emptyResultMessage: 'Nenhum concorrente encontrado',
    isClearSelectionAllowed: true,
  };

  private isLoadingOptions = false;
  private carregado = false;
  private brutos: IConcorrenteListItem[] = [];
  private destroy$ = new Subject<void>();

  constructor(private concorrenteApi: ConcorrenteApi, private toastService: ToastService) {
    super();
    this.settings = { ...this.defaultSettings };
  }

  ngOnChanges(changes: SimpleChanges): void {
    const idsMudaram =
      !!changes.idsJaSelecionados &&
      !mesmosIds(changes.idsJaSelecionados.previousValue, changes.idsJaSelecionados.currentValue);
    if (changes.concorrenteVinculado || idsMudaram) {
      this.recomporOptions();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  firstOpened(): void {
    if (!this.carregado) {
      this.load();
    }
  }

  onSelectionChange(): void {
    this.recomporOptions();
    this.emitChange(this.selectedId);
  }

  protected updateValue(newValue: number | null): void {
    this.selectedId = newValue;
    if (newValue != null && !this.carregado) {
      this.load();
    }
    this.recomporOptions();
  }

  private load(): void {
    if (this.isLoadingOptions) {
      return;
    }
    this.definirCarregando(true);
    this.concorrenteApi
      .list()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(
        (itens) => {
          this.definirCarregando(false);
          this.carregado = true;
          this.brutos = itens;
          this.recomporOptions();
        },
        (error: ApiError) => {
          this.definirCarregando(false);
          this.toastService.error(error.message, { duration: 5000 });
        }
      );
  }

  private definirCarregando(carregando: boolean): void {
    this.isLoadingOptions = carregando;
    this.settings = { ...this.defaultSettings, isLoading: this.isLoadingOptions };
  }

  private recomporOptions(): void {
    const idsExcluidos = this.idsJaSelecionados.filter((id) => id !== this.selectedId);
    const ativos = this.brutos
      .filter((item) => !idsExcluidos.includes(item.id))
      .map((item) => this.toOption(item, false));

    const vinculado = this.concorrenteVinculado;
    const deveInjetarVinculado =
      !!vinculado &&
      vinculado.situacao === EnumSituacaoConcorrente.INATIVO &&
      !ativos.some((option) => option.id === vinculado.id);

    this.options = deveInjetarVinculado ? [...ativos, this.toOption(vinculado, true)] : ativos;
  }

  private toOption(
    item: IConcorrenteListItem | IConcorrenteRef,
    inativo: boolean
  ): IConcorrenteSelectOption {
    const identidade = resolverIdentidadeConcorrente(item);
    return {
      id: item.id,
      label: `${item.codigo} - ${item.nome}`,
      icone: identidade.tipo === 'icone' ? identidade.valor : null,
      imagemUrl: identidade.tipo === 'imagem' ? identidade.valor : null,
      tipoLabel: typeof item.tipo === 'object' && item.tipo ? item.tipo.rotulo : null,
      inativo,
    };
  }
}
